import {
	asnValues,
	ispValues,
	selectedAsnNames
} from 'pages/workspace/asset/custom-rules/components/network-values'
import { CriteriaModifierType } from 'api-models'
import { RuleFormType } from './types'
import { buildCriteriaModifier } from './utils'

enum ActionTypes {
	SET_SIMPLE_VALUE = 'SET_SIMPLE_VALUE',
	SET_OBJECT_SIMPLE_VALUE = 'SET_SIMPLE_STRING',
	TOGGLE_SCOPE = 'TOGGLE_SCOPE',
	TOGGLE_ADDITIONAL_CRITERIA = 'TOGGLE_ADDITIONAL_CRITERIA',
	TOGGLE_ADDITIONAL_CRITERIA_PREFIX = 'TOGGLE_ADDITIONAL_CRITERIA_PREFIX',
	SET_ADDITIONAL_CRITERIA_VALUE = 'SET_ADDITIONAL_CRITERIA_VALUE',
	CHANGE_THRESHOLD_TYPE = 'CHANGE_THRESHOLD_TYPE',
	CHANGE_CONVERSION_MODE = 'CHANGE_CONVERSION_MODE',
	SET_THRESHOLD_PERCENTAGE_VALUE = 'SET_THRESHOLD_PERCENTAGE_VALUE',
	RESET = 'RESET',
	TOGGLE_BLOCK_ACTION = 'TOGGLE_BLOCK_ACTION',
	TOGGLE_TEMP_EXCLUSION_ACTION = 'TOGGLE_TEMP_EXCLUSION_ACTION',
	SET_PERIOD_VALUE = 'SET_PERIOD_VALUE',
	SET_PERIOD_UNIT = 'SET_PERIOD_UNIT'
}

/**
 * Actions
 */
export function setSimpleValueAction(key: string, value: string | boolean) {
	return {
		type: ActionTypes.SET_SIMPLE_VALUE,
		payload: { key, value }
	}
}
export function setObjectSimpleValueAction(
	objectName: string,
	key: string,
	value: string | number | boolean | { value: string; label: string } | string[]
) {
	return {
		type: ActionTypes.SET_OBJECT_SIMPLE_VALUE,
		payload: { objectName, key, value }
	}
}
export function toggleScopeAction(scope: string, level: string) {
	return {
		type: ActionTypes.TOGGLE_SCOPE,
		payload: { scope, level }
	}
}
export function toggleAdditionalCriteriaAction(
	criteriaType: string,
	isChecked: boolean
) {
	return {
		type: ActionTypes.TOGGLE_ADDITIONAL_CRITERIA,
		payload: { criteriaType, isChecked }
	}
}
export function toggleAdditionalCriteriaPrefixAction(
	criteriaType: CriteriaModifierType,
	criteriaKey: string,
	negative: boolean
) {
	return {
		type: ActionTypes.TOGGLE_ADDITIONAL_CRITERIA_PREFIX,
		payload: { criteriaType, criteriaKey, negative }
	}
}
export function setAdditionalCriteriaValueAction(
	criteriaType: CriteriaModifierType,
	criteriaKey: string,
	value: (string | null)[] | string,
	names?: Record<string, string>
) {
	return {
		type: ActionTypes.SET_ADDITIONAL_CRITERIA_VALUE,
		payload: { criteriaType, criteriaKey, value, names }
	}
}
export function changeThresholdTypeAction(type: string) {
	return {
		type: ActionTypes.CHANGE_THRESHOLD_TYPE,
		payload: { type }
	}
}
export function changeConversionModeAction(mode: string) {
	return {
		type: ActionTypes.CHANGE_CONVERSION_MODE,
		payload: { mode }
	}
}
export function setThresholdValueAction(value: string) {
	return {
		type: ActionTypes.SET_THRESHOLD_PERCENTAGE_VALUE,
		payload: { value }
	}
}
export function resetAction(initialState: RuleFormType) {
	return {
		type: ActionTypes.RESET,
		payload: { initialState }
	}
}
export function toggleBlockAction() {
	return {
		type: ActionTypes.TOGGLE_BLOCK_ACTION
	}
}
export function toggleTempExclusionAction() {
	return {
		type: ActionTypes.TOGGLE_TEMP_EXCLUSION_ACTION
	}
}
export function setPeriodValue(key: string, value: string) {
	return {
		type: ActionTypes.SET_PERIOD_VALUE,
		payload: { key, value }
	}
}
export function setPeriodUnit(key: string, unit: string) {
	return {
		type: ActionTypes.SET_PERIOD_UNIT,
		payload: { key, unit }
	}
}

/**
 * Reducer
 */
export function ruleReducer(rule: RuleFormType, action: any) {
	switch (action.type) {
		case ActionTypes.SET_SIMPLE_VALUE: {
			const { key, value } = action.payload

			return { ...rule, [key]: value }
		}
		case ActionTypes.SET_OBJECT_SIMPLE_VALUE: {
			const { objectName, key, value } = action.payload

			return { ...rule, [objectName]: { ...rule[objectName], [key]: value } }
		}
		case ActionTypes.TOGGLE_SCOPE: {
			const { scope, level } = action.payload

			let scopeValues = rule.trigger.scope_values || []

			if (scope === 'WEBSITE' && level === 'website') scopeValues = []
			if (scope === 'ACCOUNT' && level === 'ad-account') scopeValues = []
			if (scope === 'CAMPAIGN' && level === 'campaign') scopeValues = []

			return {
				...rule,
				trigger: { ...rule.trigger, scope, scope_values: scopeValues }
			}
		}
		case ActionTypes.TOGGLE_ADDITIONAL_CRITERIA: {
			const { criteriaType, isChecked } = action.payload
			let newModifiers = []

			if (isChecked) {
				const modifier = buildCriteriaModifier(criteriaType)
				if (modifier) {
					newModifiers = [...rule.modifiers, ...[modifier]]
					if (criteriaType === CriteriaModifierType.CONVERSION) {
						newModifiers = newModifiers.filter(
							(m) => m.type !== CriteriaModifierType.NOT_CONVERSION
						)
					}
					if (criteriaType === CriteriaModifierType.NOT_CONVERSION) {
						newModifiers = newModifiers.filter(
							(m) => m.type !== CriteriaModifierType.CONVERSION
						)
					}
				} else {
					newModifiers = [...rule.modifiers]
				}
			} else {
				newModifiers = rule.modifiers.filter((m) => m.type !== criteriaType)
			}

			return {
				...rule,
				modifiers: newModifiers
			}
		}
		case ActionTypes.TOGGLE_ADDITIONAL_CRITERIA_PREFIX: {
			const { criteriaType, criteriaKey, negative } = action.payload

			const newModifiers = rule.modifiers.map((m) => {
				if (m.type === criteriaType) {
					return { ...m, [criteriaKey]: { ...m[criteriaKey], negative } }
				} else {
					return m
				}
			})

			return { ...rule, modifiers: newModifiers }
		}
		case ActionTypes.SET_ADDITIONAL_CRITERIA_VALUE: {
			const { criteriaType, criteriaKey, value, names } = action.payload

			const newModifiers = rule.modifiers.map((m) => {
				if (m.type === criteriaType) {
					if (criteriaType === CriteriaModifierType.TIME) {
						m[criteriaKey].primary = { value, unit: 'second' }
						return m
					}
					if (criteriaType === CriteriaModifierType.PAGE_VIEWS) {
						m[criteriaKey].primary = value
						return m
					}
					if (criteriaType === CriteriaModifierType.SEARCH_KEYWORD) {
						const values = Array.isArray(value) ? value : [value]
						return {
							...m,
							keyword_text: {
								...m.keyword_text,
								values,
								value: values[0] ?? ''
							}
						}
					}
					if (criteriaType === CriteriaModifierType.ISP) {
						return { ...m, isp: { ...m.isp, values: ispValues(value) } }
					}
					if (criteriaType === CriteriaModifierType.ASN) {
						const values = asnValues(value)
						return {
							...m,
							asn: {
								...m.asn,
								values,
								names: selectedAsnNames(values, names ?? m.asn?.names)
							}
						}
					}
					if (criteriaType === CriteriaModifierType.CONVERSION) {
						m[criteriaKey].label = value
						return m
					}

					m[criteriaKey].values = value
					return m
				} else {
					return m
				}
			})

			return { ...rule, modifiers: newModifiers }
		}
		case ActionTypes.CHANGE_THRESHOLD_TYPE: {
			const { type } = action.payload

			return {
				...rule,
				threshold: { ...rule.threshold, type }
			}
		}
		case ActionTypes.CHANGE_CONVERSION_MODE: {
			const { mode } = action.payload

			const newModifiers = rule.modifiers.map((m) => {
				if (m.type === CriteriaModifierType.CONVERSION) {
					if (m.conversion) m.conversion.mode = mode
					return m
				}

				return m
			})

			return {
				...rule,
				modifiers: newModifiers
			}
		}
		case ActionTypes.SET_THRESHOLD_PERCENTAGE_VALUE: {
			const { value } = action.payload

			return {
				...rule,
				threshold: { ...rule.threshold, percentage_value: Number(value) }
			}
		}
		case ActionTypes.RESET: {
			return { ...action.payload.initialState }
		}
		case ActionTypes.TOGGLE_BLOCK_ACTION: {
			const newValue =
				rule.block === null ? { period: { value: 15, unit: 'day' } } : null

			return { ...rule, block: newValue }
		}
		case ActionTypes.TOGGLE_TEMP_EXCLUSION_ACTION: {
			const newValue =
				rule.temp_ip_exclusion === null
					? { period: { value: 5, unit: 'hour' } }
					: null

			return { ...rule, temp_ip_exclusion: newValue }
		}
		case ActionTypes.SET_PERIOD_VALUE: {
			const { key, value } = action.payload

			return {
				...rule,
				[key]: { period: { ...rule[key]?.period, value: Number(value) } }
			}
		}
		case ActionTypes.SET_PERIOD_UNIT: {
			const { key, unit } = action.payload

			return {
				...rule,
				[key]: { period: { ...rule[key]?.period, unit } }
			}
		}
		default:
			return rule
	}
}
